export type Route = 'join' | 'lobby' | 'riddle' | 'chat' | 'results' | 'spectator' | 'leaderboard';

type RouteHandler = (params?: Record<string, string>) => void;

const handlers = new Map<Route, RouteHandler>();
let currentRoute: Route = 'join';

export function onRoute(route: Route, handler: RouteHandler): void {
  handlers.set(route, handler);
}

export function navigate(route: Route, params?: Record<string, string>): void {
  currentRoute = route;
  const hash = params
    ? `#/${route}?${new URLSearchParams(params).toString()}`
    : `#/${route}`;
  // If the target hash is identical to the current one, the browser won't
  // fire a `hashchange` event, so the view never re-renders. This happens on
  // auto-advance / manual skip from the riddle view (riddle → riddle), where
  // the new state has a different currentLocationIndex but the route is the
  // same. Force a re-render by calling handleRoute() directly in that case.
  if (window.location.hash === hash) {
    handleRoute();
  } else {
    window.location.hash = hash;
  }
}

export function getCurrentRoute(): Route {
  return currentRoute;
}

function parseHash(): { route: Route; params: Record<string, string> } {
  const hash = window.location.hash.slice(2) || 'join'; // remove #/
  const [routePart, queryPart] = hash.split('?');
  const route = (routePart || 'join') as Route;
  const params: Record<string, string> = {};
  if (queryPart) {
    new URLSearchParams(queryPart).forEach((v, k) => { params[k] = v; });
  }
  return { route, params };
}

export function handleRoute(): void {
  const { route, params } = parseHash();
  currentRoute = route;
  const handler = handlers.get(route);
  if (handler) {
    handler(params);
  } else {
    // Default to join
    const joinHandler = handlers.get('join');
    if (joinHandler) joinHandler();
  }
}

export function initRouter(): void {
  window.addEventListener('hashchange', handleRoute);
  handleRoute();
}
